import React from 'react';
import { Helmet } from 'react-helmet';
import { WebPageSchema, BreadcrumbSchema } from '../components/SEOStructuredData';
import { Container, Title, Text, Grid, Timeline, ThemeIcon, List, Card, Image } from '@mantine/core';
import { motion } from 'framer-motion';
import { IconBriefcase, IconCertificate, IconUsers, IconShield } from '@tabler/icons-react';
import classes from './About.module.css';

export default function About() {
  // Define breadcrumb items for structured data
  const breadcrumbItems = [
    { name: 'Home', url: 'https://optifyne.com/' },
    { name: 'About Us', url: 'https://optifyne.com/about' }
  ];

  return (
    <>
      {/* SEO Structured Data */}
      <Helmet>
        <title>About Optifyne - Our Mission and Expertise</title>
        <meta name="description" content="Learn about Optifyne's team of security-cleared Nintex K2 and Power Platform experts delivering secure process automation for government and enterprise." />
      </Helmet>
      <WebPageSchema 
        title="About Optifyne - Our Mission and Expertise"
        description="Learn about Optifyne's team of security-cleared Nintex K2 and Power Platform experts delivering secure process automation for government and enterprise."
        url="https://optifyne.com/about"
      />
      <BreadcrumbSchema items={breadcrumbItems} />
    <div className={classes.wrapper}>
      {/* Hero Section */}
      <section className={classes.hero}>
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={classes.heroContent}
          >
            <Title className={classes.title}>
              About <span className={classes.highlight}>Optifyne</span>
            </Title>
            
            <Text className={classes.subtitle}>
              Nintex K2 Experts for Government and Private Sector Organisations
            </Text>
          </motion.div>
        </Container>
      </section>

      {/* Mission Section */}
      <section className={classes.missionSection}>
        <Container size="xl">
          <Grid gutter={{ base: 20, md: 50 }} align="stretch" grow>
            <Grid.Col span={{ base: 12, md: 7 }} order={{ base: 2, md: 1 }}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Title order={2} className={classes.sectionTitle} mb="xl">
                  Our Mission
                </Title>
                
                <Text className={classes.sectionText} size="lg" mb="lg">
                  At Optifyne, we deliver secure, scalable, and efficient business process automation using Nintex K2,
                  both on-premise and in the cloud. Our consultants have a wealth of experience, we help government agencies and private sector organisations simplify complex processes, enhance productivity, and achieve measurable outcomes.
                </Text>

                <Text className={classes.sectionText} size="lg" mb="lg">
                  Our SC Cleared Developers and BPSS Cleared Business Analysts are trusted to work on highly sensitive projects, ensuring full compliance with security standards. Whether working within the public sector or regulated private industries, Optifyne delivers innovation
                  at speed, helping you automate processes and drive transformation.
                </Text>
                <Text
            className={classes.tagline}
            mt="sm"
            fw={600}
            fs="italic"
            c="dimmed"
          >
            Simplifying Complexity, Amplifying Results
          </Text>
              </motion.div>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 5 }} order={{ base: 1, md: 2 }}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
              >
                <Image
                  src="/images/nintex_partner.png"
                  alt="Nintex Partner"
                  fit="contain"
                  style={{ maxWidth: '350px', width: '100%' }}
                />
              </motion.div>
            </Grid.Col>
          </Grid>
        </Container>
      </section>
      
      {/* Security Clearance Section */}
      <section className={classes.securitySection}>
        <Container size="xl">
          <Grid gutter={50} align="center">
            <Grid.Col md={6} orderMd={2}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Title order={2} className={classes.sectionTitle}>
                  Security-Cleared Expertise
                </Title>
                
                <Text className={classes.sectionText}>
                  Our team consists of SC Cleared Developers and BPSS Cleared Business Analysts who are 
                  trusted to work on highly sensitive projects for government agencies and regulated 
                  private sector organizations.
                </Text>
                
                <List
                  spacing="md"
                  size="lg"
                  mt={30}
                  className={classes.securityList}
                  icon={
                    <ThemeIcon size={24} radius="xl" className={classes.listIcon}>
                      <IconShield size={16} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>SC Cleared Developers for high-security environments</List.Item>
                  <List.Item>BPSS Cleared Business Analysts for sensitive projects</List.Item>
                  <List.Item>Full compliance with government security standards</List.Item>
                  <List.Item>Secure development practices and protocols</List.Item>
                  <List.Item>Data protection and governance expertise</List.Item>
                </List>
              </motion.div>
            </Grid.Col>
            
            <Grid.Col md={6} orderMd={1}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
              </motion.div>
            </Grid.Col>
          </Grid>
        </Container>
      </section>
      
      {/* Experience Timeline Section */}
      <section className={classes.timelineSection}>
        <Container size="xl">
          <Title order={2} className={classes.sectionTitle} ta="center">
            Our Experience
          </Title>
          
          <Text className={classes.sectionDescription} ta="center" maw={800} mx="auto" mb={50}>
            With over 15 years of experience in process automation and Nintex K2 development, 
            we've helped numerous government agencies and private organizations transform their operations.
          </Text>
          
          <Timeline active={4} bulletSize={24} lineWidth={2} className={classes.timeline}>
            <Timeline.Item 
              bullet={<IconBriefcase size={16} />} 
              title="Government Sector Projects"
              className={classes.timelineItem}
            >
              <Text className={classes.timelineText}>
                Delivered secure process automation solutions for multiple government departments, 
                helping streamline operations while maintaining the highest security standards.
              </Text>
            </Timeline.Item>
            
            <Timeline.Item 
              bullet={<IconUsers size={16} />} 
              title="Private Sector Innovations"
              className={classes.timelineItem}
            >
              <Text className={classes.timelineText}>
                Implemented Nintex K2 solutions for finance, healthcare, and energy companies, 
                resulting in significant efficiency gains and cost savings.
              </Text>
            </Timeline.Item>
            
            <Timeline.Item 
              bullet={<IconCertificate size={16} />} 
              title="Nintex K2 Expertise"
              className={classes.timelineItem}
            >
              <Text className={classes.timelineText}>
                Developed deep expertise in Nintex K2, becoming recognized specialists in 
                both cloud and on-premise implementations for complex business processes.
              </Text>
            </Timeline.Item>
            
            <Timeline.Item 
              bullet={<IconShield size={16} />} 
              title="Security Clearance"
              className={classes.timelineItem}
            >
              <Text className={classes.timelineText}>
                Obtained SC and BPSS clearances, allowing our team to work on highly sensitive 
                projects requiring the highest levels of security and compliance.
              </Text>
            </Timeline.Item>
          </Timeline>
        </Container>
      </section>
      
      {/* Team Values Section */}
      <section className={classes.valuesSection}>
        <Container size="xl">
          <Title order={2} className={classes.sectionTitle} ta="center" mb={30}>
            Our Values
          </Title>
          
          <Grid gutter={{ base: 20, md: 30 }}>
            {[
              {
                title: "Security First",
                description: "We prioritize security in everything we do, ensuring compliance, data protection, and governance are at the heart of every solution."
              },
              {
                title: "Technical Excellence",
                description: "We maintain the highest standards of technical expertise, continuously improving our skills to deliver the best solutions."
              },
              {
                title: "Client Partnership",
                description: "We work as partners with our clients, understanding their unique challenges and delivering solutions that meet their specific needs."
              },
              {
                title: "Agile Delivery",
                description: "Our agile approach ensures faster results with minimal disruption and maximum impact, adapting quickly to changing requirements."
              },
              {
                title: "Innovation",
                description: "We constantly explore new technologies and approaches to solve complex problems in innovative ways."
              },
              {
                title: "Knowledge Transfer",
                description: "We believe in empowering our clients through training and knowledge sharing, ensuring long-term success beyond project completion."
              }
            ].map((value, index) => (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <Card className={classes.valueCard} withBorder p="lg" radius="md" style={{ height: '100%' }}>
                    <Title order={3} className={classes.valueTitle}>
                      {value.title}
                    </Title>
                    
                    <Text className={classes.valueDescription}>
                      {value.description}
                    </Text>
                  </Card>
                </motion.div>
              </Grid.Col>
            ))}
          </Grid>
        </Container>
      </section>
    </div>
    </>
  );
}
